/**
 * B614 leg (b) — the SHOW face's tickets step (555:2 @1440, 555:201 @390).
 * A comedy club's buyer arrives from a poster's chip or a calendar tile, so
 * the night is already chosen: the step offers the nights as chips (the
 * 14-day window), never a month grid first, and speaks the club's register —
 * a HEADLINER with the lineup, the bio, the policy badges on the photo, GOOD
 * TO KNOW from the policy facts, showtimes with their own age and seats left.
 * The derivations live in lib/showTickets.ts; composition, caps and the group
 * door are TicketsStep's own (shared helpers), so both faces sell alike.
 * Non-SHOW tenants keep TicketsStep unchanged.
 */
import type { TimeFormat } from "@transact/ui";
import { useState, type ReactElement } from "react";
import { lineupLine } from "@transact/contracts";
import { PhotoCarousel, PickerRail, PickerTile, SheetCard, StickyBar, Stepper as UiStepper, TotalRow, Button } from "@transact/ui";
import { compositionIssues, issueHint, quantityOf, totalQuantity, type Composition } from "../lib/composition.js";
import { formatMoney } from "../lib/money.js";
import { pageDescription, pagePhotos, type ProductMedia } from "../lib/productPage.js";
import { groupParty, groupPanelTitle, stepperMax } from "../lib/groupBooking.js";
import { goodToKnowLines, nightWindow, picksWhen, policyBadges, showtimeHead, showtimeNote, showtimeWords } from "../lib/showTickets.js";
import { groupDepartures, seatsLeft, type DisplayOptions } from "./departures.js";
import { GroupBookingPanel } from "./groupBooking.js";
import { displayTotal } from "./ticketsStep.js";
import type { PoolAvailability, ProductSummary, SlotSummary } from "./screens.js";

export function ShowTicketsStep(props: {
  readonly card: ProductSummary;
  readonly slots: readonly SlotSummary[];
  readonly availability: Readonly<Record<string, readonly PoolAvailability[]>>;
  readonly comp: Composition;
  readonly onChange: (variantId: string, quantity: number) => void;
  readonly chosenSlotId?: string;
  readonly onPickSlot: (slotId: string) => void;
  readonly selectedDayKey?: string;
  readonly onSelectDay: (key: string) => void;
  readonly onContinue: () => void;
  readonly now: number;
  readonly busy?: boolean;
  readonly locale?: string;
  readonly timeZone?: string;
  /** B621 — the tenant's clock; absent = 12h AM/PM, the ruled default. */
  readonly timeFormat?: TimeFormat;
  readonly media?: ProductMedia;
  readonly groupThreshold?: number | undefined;
  readonly businessName?: string | undefined;
  readonly onRequestGroupQuote?: (() => void) | undefined;
}): ReactElement {
  /* 555:201 — the phone's bio fold; desktop never sees the clamp or the door. */
  const [bioOpen, setBioOpen] = useState(false);
  const opts: DisplayOptions = {
    ...(props.locale !== undefined ? { locale: props.locale } : {}),
    ...(props.timeZone !== undefined ? { timeZone: props.timeZone } : {}),
    ...(props.timeFormat !== undefined ? { timeFormat: props.timeFormat } : {}),
  };
  const policies = props.card.presentation?.policies;
  const lineup = props.card.presentation?.lineup;
  const headliner = lineup?.headliner ?? props.card.name;
  const rest = lineup !== undefined ? lineupLine(lineup) : undefined;
  const photos = pagePhotos(props.card, props.media);
  const bio = pageDescription(props.card, props.media);
  const badges = policyBadges(policies);
  const know = goodToKnowLines(policies);

  const groups = groupDepartures(props.slots, opts);
  const nights = nightWindow(groups, props.now, props.selectedDayKey);
  const night = nights.find((g) => g.key === props.selectedDayKey) ?? nights[0];
  const chosen = props.slots.find((s) => s.slotId === props.chosenSlotId);

  const issues = compositionIssues(props.card, props.comp);
  const count = totalQuantity(props.comp);
  const group = props.onRequestGroupQuote !== undefined ? groupParty(props.card, props.comp, props.groupThreshold) : undefined;
  const total = displayTotal(props.card, props.comp);
  const when = picksWhen(props.card, props.comp, chosen?.start, opts);
  const missing = [...(count === 0 ? ["tickets"] : []), ...(chosen === undefined ? ["a showtime"] : [])];
  const gated = missing.length > 0 || issues.length > 0;
  const caps = props.card.variants.map((v) => v.constraints?.maxPerOrder);
  const sharedCap = caps.length > 0 && caps.every((c) => c !== undefined && c === caps[0]) ? caps[0] : undefined;
  const limit = [
    ...(sharedCap !== undefined ? [`Up to ${String(sharedCap)} per order`] : []),
    ...(props.groupThreshold !== undefined && props.onRequestGroupQuote !== undefined ? [groupPanelTitle(props.groupThreshold)] : []),
  ].join(" · ");

  return (
    <div className="show-tickets">
      <SheetCard className="show-tickets-show" redline="show-tickets-card">
        {photos.length > 0 ? (
          <PhotoCarousel subject={headliner} photos={photos} badges={badges} redline="product-photos" />
        ) : badges.length > 0 ? (
          <ul className="show-tickets-badges">
            {badges.map((b) => (
              <li key={b}>
                <span className="show-tickets-badge">{b}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <header className="show-tickets-bill">
          <p className="show-tickets-eyebrow">Headliner</p>
          <h2 className="show-tickets-title">{headliner}</h2>
          {rest !== undefined ? <p className="show-tickets-lineup">{rest}</p> : null}
        </header>
        {bio !== undefined ? (
          <>
            {/* 555:201 — on a phone the bio folds to two lines behind MORE ▾
                (the fold is a 600px stylesheet rule; the door hides at
                desktop, where the frame draws the whole bio). */}
            <p className={`show-tickets-bio${bioOpen ? "" : " show-tickets-bio--folded"}`} data-redline="product-description">
              {bio}
            </p>
            <div className="show-tickets-more">
              <Button variant="textlink-accent" expanded={bioOpen} onClick={() => setBioOpen(!bioOpen)}>
                {bioOpen ? "Less ▴" : "More ▾"}
              </Button>
            </div>
          </>
        ) : null}
      </SheetCard>

      <SheetCard className="show-tickets-pick" redline="show-tickets-pick">
        {group !== undefined ? (
          <GroupBookingPanel threshold={group.size} businessName={props.businessName} cap={group.cap} />
        ) : null}
        <section aria-labelledby="show-nights-label">
          <p className="tickets-label" id="show-nights-label">
            Pick a night
          </p>
          <PickerRail describedBy="show-nights-label">
            {nights.map((g) => (
              <li key={g.key}>
                <PickerTile
                  compact
                  value={`${g.weekday} ${g.dayOfMonth}`.toUpperCase()}
                  label={g.label}
                  pressed={g.key === night?.key}
                  onPress={() => props.onSelectDay(g.key)}
                />
              </li>
            ))}
          </PickerRail>
        </section>
        {night !== undefined && night.departures[0] !== undefined ? (
          <section aria-labelledby="show-times-label">
            <p className="tickets-label" id="show-times-label">
              {showtimeHead(night.departures[0].start, opts)}
            </p>
            <PickerRail describedBy="show-times-label">
              {night.departures.map((d) => {
                const pools = props.availability[d.slotId];
                const left = pools === undefined ? undefined : seatsLeft(pools);
                const note = showtimeNote(d, policies?.minAge, pools);
                return (
                  <li key={d.slotId}>
                    <PickerTile
                      compact
                      value={showtimeWords(d.start, opts)}
                      {...(note !== undefined ? { note, ...(left !== undefined && left <= 0 ? { noteTone: "low" as const } : {}) } : {})}
                      pressed={d.slotId === props.chosenSlotId}
                      disabled={left !== undefined && left <= 0}
                      onPress={() => props.onPickSlot(d.slotId)}
                    />
                  </li>
                );
              })}
            </PickerRail>
          </section>
        ) : null}
        <section aria-labelledby="show-types-label" className="show-tickets-types">
          <p className="tickets-label" id="show-types-label">
            Tickets
          </p>
          <ul className="tickets-rows">
            {props.card.variants.map((v) => (
              <li key={v.variantId} className="tickets-row">
                <span className="tickets-row-name">
                  {v.name}
                  {v.description !== undefined ? <span className="show-tickets-row-meaning">{v.description}</span> : null}
                </span>
                {v.displayPrice !== undefined ? <span className="tickets-row-price">{formatMoney(v.displayPrice)}</span> : null}
                <UiStepper
                  frame="sheet"
                  label={v.name}
                  value={quantityOf(props.comp, v.variantId)}
                  min={0}
                  max={stepperMax(props.card, v.variantId, props.groupThreshold, count, quantityOf(props.comp, v.variantId))}
                  onChange={(next) => props.onChange(v.variantId, next)}
                  decreaseLabel={`Fewer ${v.name}`}
                  increaseLabel={`More ${v.name}`}
                />
              </li>
            ))}
          </ul>
          {limit !== "" ? <p className="tickets-footnote">{limit}</p> : null}
          {issues.length > 0 ? (
            <div id="show-tickets-issues">
              {issues.map((issue) => (
                <p key={`${issue.kind}:${issue.variantId}`} className="hint" role="status">
                  {issueHint(issue, props.card)}
                </p>
              ))}
            </div>
          ) : null}
        </section>
        <section aria-labelledby="show-picks-label" className="show-tickets-picks">
          <p className="tickets-label" id="show-picks-label">
            Your picks
          </p>
          {count === 0 ? (
            <p className="product-picks-empty">No tickets picked yet.</p>
          ) : (
            <div className="show-tickets-picked">
              <p className="show-tickets-picked-name">{headliner}</p>
              {when !== undefined ? <p className="show-tickets-picked-when">{when}</p> : null}
            </div>
          )}
          {total !== undefined && count > 0 ? <TotalRow label="Total" amount={formatMoney(total)} /> : null}
        </section>
      </SheetCard>

      {know.length > 0 ? (
        <SheetCard className="show-tickets-know" redline="show-tickets-know">
          <p className="tickets-label">Good to know</p>
          <ul className="show-tickets-know-list">
            {know.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </SheetCard>
      ) : null}

      <StickyBar
        redline="tickets-sticky"
        seatedWhenWide
        summary={gated ? (issues.length > 0 ? undefined : `Pick ${missing.join(" and ")} to continue.`) : [when, total !== undefined ? formatMoney(total) : undefined].filter((x) => x !== undefined).join(" · ")}
      >
        {group !== undefined ? (
          <Button size="lg" busy={props.busy === true} onClick={() => props.onRequestGroupQuote?.()}>
            Request a group quote
          </Button>
        ) : (
          <Button
            size="lg"
            disabled={gated}
            busy={props.busy === true}
            {...(gated && issues.length > 0 ? { describedBy: "show-tickets-issues" } : {})}
            onClick={props.onContinue}
          >
            Continue to cart
          </Button>
        )}
      </StickyBar>
    </div>
  );
}
